/**
 * Quality NCR list column configuration.
 *
 * Mirrors the Schedule Lines column-manager pattern (types.ts +
 * load/save + drag reorder). Column set sourced from the legacy
 * SharePoint Corrective/Preventative Action List that customers
 * still expect parity with.
 *
 * The 5 columns marked `dataMissing: true` are intentionally listed
 * with a "—" placeholder render — the underlying NCR model doesn't
 * carry the value yet, but the column is exposed so the customer
 * can see what's expected and request the model addition.
 */
import type { ReactNode } from 'react';
import type { NonConformanceReport } from '../../lib/api/types/quality';
import { NCRStateBadge, ProcessCell } from './ncrPlate';
import { ncrHasSupplier, ncrSupplierLabel } from './ncrNoSupplier';

export interface NCRColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  /** API ordering field name when sortable. Falls back to id. */
  sortField?: string;
  minWidth: string;
  order: number;
  /** True when the spec column has no backing model data — renders as "—". */
  dataMissing?: boolean;
}

export const NCR_COLUMN_STORAGE_KEY = 'quality_ncr_column_config';

/**
 * Default column set — order, visibility, and labels match the
 * Dennis Eagle SharePoint export (`Columns.xlsx`). Visibility off-by-default
 * for less-used columns to keep first-load uncluttered.
 */
// T12 (QUALITY-NCR-REVIEW, Aaron 30 Sep 2026): visible by default in the spec's
// order (SPEC-DIGEST §2) plus GRN / PO / PO Line and Replacement; detail
// columns (description, disposition, quantities, money, grades) are in Columns.
export const DEFAULT_NCR_COLUMNS: NCRColumnConfig[] = [
  { id: 'ncr_number', label: 'NCR No.', visible: true, sortable: true, sortField: 'ncr_number', minWidth: '110px', order: 0 },
  // QUALITY-NCR-REVIEW #17: the date filter and the default sort both use NCR
  // Date, so that is the date the grid shows; "Created" (when the record was
  // entered) stays available.
  { id: 'ncr_date', label: 'NCR Date', visible: true, sortable: true, sortField: 'ncr_date', minWidth: '120px', order: 12 },
  { id: 'raised_date', label: 'Created', visible: false, sortable: true, sortField: 'raised_date', minWidth: '140px', order: 1.5 },
  { id: 'raised_by_name', label: 'Raised by', visible: true, sortable: false, minWidth: '160px', order: 10 },
  // CR-6027: surface the NCR's customer company directly in the list so
  // reviewers don't have to open each record to see who raised it.
  // Backed by `company_name` (source='company.name') on
  // NonConformanceReportListSerializer; ordering goes through
  // company__name which is already in the ViewSet's ordering_fields.
  { id: 'company_name', label: 'Customer', visible: true, sortable: true, sortField: 'company_name', minWidth: '180px', order: 1 },
  { id: 'supplier_code', label: 'Supplier Code', visible: false, sortable: false, minWidth: '120px', order: 3.5 },
  { id: 'supplier_name', label: 'Supplier', visible: true, sortable: true, sortField: 'supplier_name', minWidth: '180px', order: 3 },
  // Supplier quality master data, uploaded against supplier code. These read
  // from the supplier company, so they populate on every NCR for that supplier
  // — including ERP-imported ones — with no per-NCR data entry.
  { id: 'supplier_uk_eu', label: 'UK/EU', visible: false, sortable: false, minWidth: '90px', order: 3.6 },
  { id: 'part_number', label: 'Part No.', visible: true, sortable: true, sortField: 'part_number', minWidth: '120px', order: 5 },
  // QUALITY-NCR-REVIEW #17: order references the list serializer already
  // carries, now choosable as columns.
  { id: 'customer_reference', label: 'Customer NCR No.', visible: true, sortable: false, minWidth: '140px', order: 2 },
  { id: 'grn_number', label: 'GRN No.', visible: true, sortable: false, minWidth: '110px', order: 6 },
  { id: 'po_number', label: 'PO No.', visible: true, sortable: false, minWidth: '110px', order: 7 },
  { id: 'po_line_number', label: 'PO Line No.', visible: true, sortable: false, minWidth: '80px', order: 8 },
  { id: 'buyer_name', label: 'Buyer', visible: true, sortable: false, minWidth: '140px', order: 9 },
  { id: 'works_order', label: 'Works Order No.', visible: false, sortable: false, minWidth: '130px', order: 17.1 },
  { id: 'packing_note_no', label: 'Packing Note No.', visible: true, sortable: false, minWidth: '130px', order: 11 },
  { id: 'defect_description', label: 'Defect Description', visible: false, sortable: false, minWidth: '240px', order: 17.2 },
  { id: 'liability_display', label: 'Liability', visible: true, sortable: false, minWidth: '130px', order: 4 },
  // CR-7349: where the non-conformance was received (merged 30 Sep; detail, so in Columns).
  { id: 'warehouse_display', label: 'Warehouse', visible: false, sortable: false, minWidth: '160px', order: 17.05 },
  { id: 'is_internal', label: 'Internal', visible: false, sortable: false, minWidth: '90px', order: 9.1 },
  { id: 'assigned_to_name', label: 'Assigned To', visible: false, sortable: false, minWidth: '160px', order: 9.2 },
  { id: 'proposed_rejection_display', label: 'Action to Resolve', visible: false, sortable: false, minWidth: '160px', order: 17.3 },
  { id: 'collection_date', label: 'Collection Date', visible: false, sortable: false, minWidth: '130px', order: 11.5 },
  // Spec list (24 Sep 2026): top-level Status (Open / Closed / Deleted) and one
  // column per process with its sub-status and who acts next -- (S) supplier /
  // (C) customer. The raw workflow stage stays available, off by default.
  { id: 'ncr_state', label: 'Status', visible: true, sortable: true, sortField: 'status', minWidth: '100px', order: 17 },
  { id: 'plate_eightd', label: '8D', visible: true, sortable: false, minWidth: '150px', order: 13 },
  { id: 'plate_collection', label: 'Collection', visible: true, sortable: false, minWidth: '150px', order: 14 },
  { id: 'plate_credit', label: 'Credit', visible: true, sortable: false, minWidth: '150px', order: 15 },
  { id: 'plate_replacement', label: 'Replacement', visible: true, sortable: false, minWidth: '150px', order: 16 },
  // Legacy parity: the days-waiting counters (off by default).
  { id: 'dw_containment', label: 'Days Waiting (Containment)', visible: false, sortable: false, minWidth: '150px', order: 11.95 },
  { id: 'dw_corrective_action', label: 'Days Waiting (CAR)', visible: false, sortable: false, minWidth: '140px', order: 11.96 },
  { id: 'dw_collection', label: 'Days Waiting for Collection', visible: false, sortable: false, minWidth: '170px', order: 11.97 },
  { id: 'dw_credit', label: 'Days Outstanding Credit', visible: false, sortable: false, minWidth: '160px', order: 11.98 },
  { id: 'status_display', label: 'Workflow Stage', visible: false, sortable: true, sortField: 'status', minWidth: '120px', order: 12.5 },
  { id: 'immediate_corrective_action', label: 'Immediate Containment Action', visible: false, sortable: false, minWidth: '260px', order: 17.7 },
  // CR-7353: the root cause split into an Occurrence chain and an Escape chain.
  { id: 'selected_root_cause', label: 'Occurrence Root Cause', visible: false, sortable: false, minWidth: '240px', order: 13.1 },
  { id: 'escape_selected_root_cause', label: 'Escape Root Cause', visible: false, sortable: false, minWidth: '240px', order: 13.15 },
  // CR-7354: the pair became four — Occurrence CA/PA + Escape CA/PA.
  { id: 'corrective_action', label: 'Occurrence Corrective Action', visible: false, sortable: false, minWidth: '240px', order: 13.2 },
  { id: 'preventive_action', label: 'Occurrence Preventive Action', visible: false, sortable: false, minWidth: '240px', order: 13.3 },
  { id: 'escape_corrective_action', label: 'Escape Corrective Action', visible: false, sortable: false, minWidth: '240px', order: 13.32 },
  { id: 'escape_preventive_action', label: 'Escape Preventive Action', visible: false, sortable: false, minWidth: '240px', order: 13.34 },
  { id: 'supplier_contact_name', label: 'Supplier Contact', visible: false, sortable: false, minWidth: '180px', order: 3.7 },
  { id: 'supplier_contact_email', label: 'Supplier Contact Email', visible: false, sortable: false, minWidth: '200px', order: 14.1 },
  // The customer's engineer who owns this supplier — CR-6794 resolves it per
  // customer, so two customers sharing a supplier no longer see one another's.
  // quality_engineer_feedback is a different thing (8D prose on the NCR) and
  // keeps its own column below.
  { id: 'supplier_quality_engineer', label: 'Quality Engineer', visible: false, sortable: false, minWidth: '180px', order: 3.8 },
  // CR-7345: the supporting quality engineer, resolved from the supplier's
  // profile just like Quality Engineer above.
  { id: 'supplier_support_quality_engineer', label: 'Support Quality Engineer', visible: false, sortable: false, minWidth: '190px', order: 15.05 },
  { id: 'quality_engineer_feedback', label: 'Quality Engineer Feedback', visible: false, sortable: false, minWidth: '200px', order: 15.1 },
  { id: 'reject_qty', label: 'Reject Qty', visible: false, sortable: false, minWidth: '90px', order: 17.4 },
  { id: 'unit_price', label: 'Unit Cost', visible: false, sortable: false, minWidth: '100px', order: 17.5 },
  { id: 'reject_value', label: 'Total Price', visible: false, sortable: false, minWidth: '110px', order: 17.6 },
  { id: 'commodity', label: 'Commodity', visible: false, sortable: false, minWidth: '160px', order: 19, dataMissing: true },
  { id: 'cost_of_rework', label: 'Cost of Rework', visible: false, sortable: false, minWidth: '120px', order: 20, dataMissing: true },
  { id: 'is_8d_required', label: '8D/A3 Required?', visible: false, sortable: false, minWidth: '100px', order: 21 },
  { id: 'date_8d_raised', label: 'Date 8D/A3 raised', visible: false, sortable: false, minWidth: '150px', order: 22 },
  { id: 'ca_submitted_date', label: 'Containment Date', visible: false, sortable: false, minWidth: '150px', order: 23 },
  // Sourced from car_submitted_date (when the CAR/8D was submitted) — DE calls
  // this the 8D/A3 submission date (customer review 2026-07-15).
  { id: 'car_submitted_date', label: '8D/A3 Submission Date', visible: false, sortable: false, minWidth: '180px', order: 24 },
  { id: 'tech_days', label: 'Tech Days', visible: false, sortable: false, minWidth: '100px', order: 24.1 },
  { id: 'engineer_days', label: "Engineer's Days", visible: false, sortable: false, minWidth: '110px', order: 24.2 },
  // 8D scoring: customer CAR grades + calculated columns.
  // RC / CA / 8D-A3 scores are shown by default (customer review 2026-07-15):
  // the scoring feature is always-on, so the register surfaces the grades
  // without the customer having to enable the columns manually.
  // CR-7357: four grades — ORC (rc_score), ERC, OPA (ca_score), EPA.
  { id: 'rc_score', label: 'ORC Score', visible: false, sortable: false, minWidth: '90px', order: 24.3 },
  { id: 'erc_score', label: 'ERC Score', visible: false, sortable: false, minWidth: '90px', order: 24.32 },
  { id: 'ca_score', label: 'OPA Score', visible: false, sortable: false, minWidth: '90px', order: 24.4 },
  { id: 'epa_score', label: 'EPA Score', visible: false, sortable: false, minWidth: '90px', order: 24.42 },
  { id: 'close_off_days_8d', label: '8D/A3 Close off days', visible: false, sortable: false, minWidth: '150px', order: 24.5 },
  { id: 'action_completion_date', label: '8D/A3 Action Completion Date', visible: false, sortable: false, minWidth: '190px', order: 24.65 },
  { id: 'score_8d', label: '8D/A3 Score', visible: false, sortable: false, minWidth: '100px', order: 24.6 },
  // Customer review 2026-07: commonised CA + repeat/containment flags
  { id: 'commonised_corrective_action_name', label: 'Commonised Corrective Action', visible: false, sortable: false, minWidth: '220px', order: 24.7 },
  { id: 'is_repeat_issue', label: 'Repeat Issue', visible: false, sortable: false, minWidth: '110px', order: 24.8 },
  { id: 'containment_required', label: 'Containment Requested', visible: false, sortable: false, minWidth: '170px', order: 24.9 },
  { id: 'credit_debit_no', label: 'Credit / Debit No.', visible: false, sortable: false, minWidth: '140px', order: 25 },
  { id: 'debit_note_raised', label: 'Debit Note Raised', visible: false, sortable: false, minWidth: '140px', order: 26 },
  { id: 'defect_photo_count', label: 'Defect Photo', visible: false, sortable: false, minWidth: '110px', order: 27 },
  { id: 'is_credit_required', label: 'Claim Status', visible: false, sortable: false, minWidth: '120px', order: 28 },
  { id: 'closed_date', label: 'Date Closed', visible: false, sortable: true, sortField: 'closed_date', minWidth: '130px', order: 29 },
  { id: 'last_modified_by_name', label: 'Modified By', visible: false, sortable: false, minWidth: '160px', order: 30 },
  { id: 'latest_supplier_comment', label: 'Supplier Comments', visible: false, sortable: false, minWidth: '260px', order: 31 },
  { id: 'quantity_scrapped', label: 'Scrapped Qty', visible: false, sortable: false, minWidth: '110px', order: 32 },
];

export function loadNCRColumns(): NCRColumnConfig[] {
  try {
    const stored = localStorage.getItem(NCR_COLUMN_STORAGE_KEY);
    // T12 (30 Sep 2026): a first visit gets the same `order` a saved layout
    // does. It used to return the array as declared, so the register's order
    // on day one was the declaration order, not the order values.
    if (!stored) return [...DEFAULT_NCR_COLUMNS].sort((a, b) => a.order - b.order);
    const parsed = JSON.parse(stored) as NCRColumnConfig[];
    const storedMap = new Map(parsed.map((c) => [c.id, c]));
    return DEFAULT_NCR_COLUMNS.map((def) => {
      const saved = storedMap.get(def.id);
      return saved ? { ...def, visible: saved.visible, order: saved.order } : def;
    }).sort((a, b) => a.order - b.order);
  } catch {
    return [...DEFAULT_NCR_COLUMNS].sort((a, b) => a.order - b.order);
  }
}

export function saveNCRColumns(cols: NCRColumnConfig[]) {
  localStorage.setItem(NCR_COLUMN_STORAGE_KEY, JSON.stringify(cols));
}

/** Default name for the customer's own NCR number (server default too). */
export const DEFAULT_CUSTOMER_REFERENCE_LABEL = 'Customer NCR No.';

/**
 * E4 (29 Sep 2026): the heading for the customer's own NCR number. Each
 * customer names it (DMR No., SRN No., ...) and every NCR carries its
 * customer's name for it; a register mixing customers falls back to the
 * generic name.
 *
 * @param rows - the NCRs on screen
 * @returns the one name every row shares, else the default
 */
export function customerReferenceLabel(rows: Pick<NonConformanceReport, 'customer_reference_label'>[]): string {
  const names = new Set(rows.map((r) => r.customer_reference_label).filter((n): n is string => !!n));
  return names.size === 1 ? Array.from(names)[0] : DEFAULT_CUSTOMER_REFERENCE_LABEL;
}

/**
 * Render the cell value for a given column. Returns ReactNode so callers can
 * pass formatters (date, number) without per-row branching at the call site.
 *
 * `dataMissing` columns short-circuit to the dash so the cell remains
 * visually present but obviously empty — pairs with the column-manager
 * dropdown footnote so the customer knows it's expected.
 */
export function renderNCRCell(
  col: NCRColumnConfig,
  ncr: NonConformanceReport,
  helpers: {
    formatDate: (d: string | Date) => string;
    formatNumber: (n: number, showDecimals?: boolean) => string;
  },
): ReactNode {
  if (col.dataMissing) return <span className="text-muted-foreground/50">&mdash;</span>;
  const dash = <span className="text-muted-foreground/50">&mdash;</span>;
  const val = (ncr as unknown as Record<string, unknown>)[col.id];
  switch (col.id) {
    case 'ncr_number':
      // Title in the spec is always the user-facing NCR number; fall back to
      // a dash rather than the DB primary key so the table never shows
      // a 5-digit internal id where users expect a "70350"-style identifier.
      // E30: an internal NCR says so on its row (suppliers never see one).
      return (
        <span className="inline-flex items-center gap-2">
          {ncr.ncr_number || dash}
          {ncr.is_internal && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">Internal</span>
          )}
        </span>
      );
    case 'supplier_name':
      // quality.no_supplier: an internal reject with no supplier reads as such.
      return ncrHasSupplier(ncr)
        ? (ncr.supplier_name || dash)
        : <span className="italic text-muted-foreground">{ncrSupplierLabel(ncr)}</span>;
    case 'raised_date':
      return ncr.raised_date ? helpers.formatDate(ncr.raised_date) : dash;
    case 'ncr_date':
      return ncr.ncr_date ? helpers.formatDate(ncr.ncr_date) : dash;
    case 'ncr_state':
      return (
        <NCRStateBadge
          state={ncr.ncr_state}
          readyToClose={ncr.status !== 'draft' && (ncr.outstanding_processes ?? []).length === 0}
        />
      );
    case 'plate_eightd':
      return <ProcessCell cell={ncr.process_plate?.eightd} />;
    case 'plate_collection':
      return <ProcessCell cell={ncr.process_plate?.collection} />;
    case 'plate_credit':
      return <ProcessCell cell={ncr.process_plate?.credit} />;
    case 'plate_replacement':
      return <ProcessCell cell={ncr.process_plate?.replacement} />;
    case 'dw_containment':
    case 'dw_corrective_action':
    case 'dw_collection':
    case 'dw_credit': {
      const key = col.id.slice(3) as 'containment' | 'corrective_action' | 'collection' | 'credit';
      const d = ncr.days_waiting?.[key];
      if (!d) return dash;
      return <span className={d.running ? 'font-medium tabular-nums' : 'tabular-nums text-muted-foreground'}>
        {d.days}{d.running ? '' : ' (done)'}
      </span>;
    }
    case 'closed_date':
      return ncr.closed_date ? helpers.formatDate(ncr.closed_date) : dash;
    case 'collection_date':
      return ncr.collection_date ? helpers.formatDate(ncr.collection_date) : dash;
    case 'date_8d_raised':
      return ncr.date_8d_raised ? helpers.formatDate(ncr.date_8d_raised) : dash;
    case 'ca_submitted_date':
      return ncr.ca_submitted_date ? helpers.formatDate(ncr.ca_submitted_date) : dash;
    case 'car_submitted_date':
      return ncr.car_submitted_date ? helpers.formatDate(ncr.car_submitted_date) : dash;
    case 'action_completion_date':
      return ncr.action_completion_date ? helpers.formatDate(ncr.action_completion_date) : dash;
    case 'reject_qty':
    case 'quantity_scrapped':
    case 'quantity_reworked':
      return typeof val === 'number' ? helpers.formatNumber(val) : dash;
    // 8D scoring: plain integers (scores 1-5, working-day count, 8D Score sum)
    case 'rc_score':
    case 'erc_score':
    case 'ca_score':
    case 'epa_score':
    case 'close_off_days_8d':
    case 'score_8d':
      return typeof val === 'number' ? String(val) : dash;
    // QUALITY-NCR-REVIEW #10 (W4): money keeps its pence -- 10.92 used to
    // read "11" and 21.84 "22". NCR.unit_price is a 2dp field.
    case 'unit_price':
      return ncr.unit_price ? helpers.formatNumber(parseFloat(ncr.unit_price), true) : dash;
    case 'reject_value':
      return typeof ncr.reject_value === 'number' ? helpers.formatNumber(ncr.reject_value, true) : dash;
    case 'is_8d_required':
      return ncr.is_8d_required ? 'Yes' : 'No';
    case 'debit_note_raised':
      return ncr.debit_note_raised ? 'Yes' : 'No';
    case 'is_repeat_issue':
      return ncr.is_repeat_issue
        ? <span className="text-red-700 font-medium">Yes</span>
        : 'No';
    case 'containment_required':
      return ncr.containment_required
        ? (ncr.containment_confirmed ? 'Contained' : 'Requested')
        : dash;
    case 'is_internal':
      return ncr.is_internal ? 'Internal' : dash;
    case 'is_credit_required': {
      // "Claim Status": a claim is resolved by EITHER a credit or a
      // replacement, so the column is Required when either flag is set.
      // Customer review 2026-07-08 (DMR 71215): the column now PROGRESSES —
      // once every required claim workflow reaches its terminal state it
      // shows Processed instead of sitting on Required forever. The
      // 'Processed' stamp comes from the customer approving the credit
      // note / replacement receipt in Helix (there is no automatic
      // disposition feed from the ERP).
      const creditNeeded = !!ncr.is_credit_required;
      const replacementNeeded = !!ncr.is_replacement_required;
      if (!creditNeeded && !replacementNeeded) return 'Not Required';
      const creditDone = !creditNeeded || !!ncr.credit_completed;
      const replacementDone = !replacementNeeded || !!ncr.replacement_completed;
      return (creditDone && replacementDone)
        ? <span className="text-emerald-700 font-medium">Processed</span>
        : 'Required';
    }
    case 'defect_description':
    case 'immediate_corrective_action':
    case 'selected_root_cause':
    case 'escape_selected_root_cause':
    case 'corrective_action':
    case 'preventive_action':
    case 'escape_corrective_action':
    case 'escape_preventive_action':
    case 'latest_supplier_comment':
    case 'quality_engineer_feedback':
    case 'supplier_uk_eu':
    case 'supplier_contact_name':
    case 'supplier_quality_engineer':
    case 'supplier_support_quality_engineer':
    case 'warehouse_display':
    case 'supplier_contact_email': {
      const text = String(val ?? '').trim();
      if (!text) return dash;
      // Truncate long text to keep rows scannable; full value stays in tooltip.
      return <span title={text}>{text.length > 80 ? `${text.slice(0, 78)}…` : text}</span>;
    }
    case 'defect_photo_count': {
      const n = ncr.defect_photo_count ?? 0;
      return n > 0 ? `${n} photo${n === 1 ? '' : 's'}` : dash;
    }
    default:
      if (val === null || val === undefined || val === '') return dash;
      return String(val);
  }
}
